import { type FC } from "react";
import { type IconProp } from "@fortawesome/fontawesome-svg-core";
import { useShopfrontDispatch, useShopfrontSelector } from "#/_Bootstrap/hooks.ts";
import {
    deleteNotification,
    markNotificationAsRead,
    type NotificationType,
} from "#/Actions/Notifications/NotificationActions.ts";
import Duration from "../../../Common/Duration/Duration";
import FontAwesomeLight from "../../../Utilities/Platform/FontAwesome/FontAwesomeLight";

interface Props {
    notification: NotificationType;
}

export const Notification: FC<Props> = ({ notification }) => {
    const dispatch = useShopfrontDispatch();

    const disable = useShopfrontSelector(state => !state.layout.notifications.open);

    return (
        <button
            type="button"
            className={
                "w-full flex items-center text-left p-4 rounded-lg cursor-pointer bg-transparent " +
                "hover:bg-black/10 dark:hover:bg-white/10 text-black dark:text-white"
            }
            onClick={() => {
                dispatch(markNotificationAsRead(notification.id));

                window.location.href = notification.data.address;
            }}
            data-testid={`notification-${notification.id}`}
        >
            <div className="text-3xl mr-4 ml-2 text-primary-dark dark:text-primary">
                <FontAwesomeLight icon={(notification.data.image ?? "question") as IconProp} />
            </div>
            <div className="flex-1 relative">
                <div className="text-base pr-3 wrap-anywhere">
                    {notification.data.title}
                </div>
                <div>
                    <Duration
                        timestamp={notification.createdAt}
                        disabled={disable}
                        uppercase
                    />
                </div>
                <div className="mt-2 text-sm wrap-anywhere">
                    {notification.data.message}
                </div>
                {notification.readAt === null && (
                    <div
                        className={
                            "absolute group right-0 top-0 w-4 h-4 p-1 rounded-full bg-transparent hover:bg-black/5 " +
                            "dark:hover:bg-white/5"
                        }
                        data-testid={`notification-unread-${notification.id}`}
                        onClick={(e) => {
                            e.stopPropagation();

                            dispatch(markNotificationAsRead(notification.id));
                        }}
                    >
                        <div className="w-2 h-2 rounded-full bg-primary dark:bg-primary-dark" />
                    </div>
                )}
            </div>
            <div
                className={
                    "text-lg ml-4 text-black dark:text-white hover:text-red-800 dark:hover:text-red-200 " +
                    "cursor-pointer p-2 hover:bg-black/5 dark:hover:bg-white/5 rounded-lg"
                }
                data-testid={`notification-delete-${notification.id}`}
                onClick={(e) => {
                    e.stopPropagation();

                    dispatch(deleteNotification(notification.id));
                }}
            >
                <FontAwesomeLight icon="trash-alt" />
            </div>
        </button>
    );
};
